/**
 * The four things the route list needs from the canvas — fit, fly to, where the
 * map is looking, and highlight these features — published by the map and read
 * by whoever draws the list.
 *
 * It exists because the two moved apart: the waypoint list used to live inside
 * `MapView` (as a side panel and, on a phone, as a bottom sheet), and in the
 * Aether shell it is the inspector's Strecke tab, beside the map rather than on
 * it. Passing four callbacks through the workspace would have made every screen
 * that shows a map own the map's internals; a bus keeps `MapView` the only file
 * that knows what MapLibre is.
 *
 * Outside a provider every call is a no-op, so the panel renders and edits the
 * route perfectly well on a screen with no map at all.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';

export interface MapApi {
  /** frame the whole route */
  fit(): void;
  flyTo(lat: number, lon: number): void;
  /** where the canvas is centred, for "add a waypoint here" */
  centre(): { lat: number; lon: number } | null;
  /** paint these feature ids as the current selection */
  setHighlight(ids: string[]): void;
}

const NOOP: MapApi = {
  fit: () => undefined,
  flyTo: () => undefined,
  centre: () => null,
  setHighlight: () => undefined,
};

interface Bridge {
  api: MapApi;
  /** the drawn route's length, for the sheet's one-line summary */
  totalNm: number;
  publish(api: MapApi | null): void;
  publishTotalNm(nm: number): void;
}

const Ctx = createContext<Bridge | null>(null);

export function MapBridgeProvider({ children }: { children: ReactNode }) {
  const [api, setApi] = useState<MapApi | null>(null);
  const [totalNm, setTotalNm] = useState(0);
  const publish = useCallback((next: MapApi | null) => setApi(next), []);
  const publishTotalNm = useCallback((nm: number) => setTotalNm(v => (v === nm ? v : nm)), []);
  const value = useMemo<Bridge>(
    () => ({ api: api ?? NOOP, totalNm, publish, publishTotalNm }),
    [api, totalNm, publish, publishTotalNm],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The map says what it can do. `api` must be memoised by the caller. */
export function usePublishMap(api: MapApi | null, totalNm: number): void {
  const bridge = useContext(Ctx);
  const publish = bridge?.publish;
  const publishTotalNm = bridge?.publishTotalNm;
  useEffect(() => {
    if (!publish) return;
    publish(api);
    return () => publish(null);
  }, [publish, api]);
  useEffect(() => {
    publishTotalNm?.(totalNm);
  }, [publishTotalNm, totalNm]);
}

/** What the map can do, or four no-ops where there is no map. */
export function useMapApi(): MapApi {
  return useContext(Ctx)?.api ?? NOOP;
}

export function useRouteTotalNm(): number {
  return useContext(Ctx)?.totalNm ?? 0;
}
